import type {
  AnnotationItem,
  Gender,
  MChartDto,
  MChartSectionDto,
  MChartVersionDto,
  UpdateArgMeasurement,
  UpdateArgSection,
} from '@/ApiClient';
import { useHierarchy } from '@/store/hierarchy.ts';
import { useMChartStatuses } from '@/store/mchart-statuses';
import { useMeasurements } from '@/store/measurements';
import { useArticles } from '@/store/articles';
import { useSizeSystems } from '@/store/size-systems';
import cloneDeep from 'lodash/cloneDeep';
import maxBy from 'lodash/maxBy';
import { parseErrorPayload } from '@lib/api-error';
import { showSnackBarNotification } from '@lib/notifications';

export function makeAnnotationLabelFn(annotations: AnnotationItem[]) {
  const measurementsStore = useMeasurements();
  return (annotation: AnnotationItem, index: number): string => {
    if (annotation.linkedEntityId) {
      return measurementsStore.byId[annotation.linkedEntityId]?.annotationName ?? '';
    }
    let unlinkedCount = 0;
    for (let i = 0; i <= index; i++) {
      if (!annotations[i]?.linkedEntityId) unlinkedCount++;
    }
    return `#${unlinkedCount}`;
  };
}

export interface AnnotationComment {
  label: string;
  comment: string;
  measurementId: number | null;
  imageIndex: number;
  annotationIndex: number;
  isReportImage?: boolean;
}

export function extractAnnotationComments(images: { annotations?: AnnotationItem[] }[]): AnnotationComment[] {
  const measurementsStore = useMeasurements();
  const comments: AnnotationComment[] = [];
  for (let imgIdx = 0; imgIdx < images.length; imgIdx++) {
    const annotations = images[imgIdx].annotations ?? [];
    let unlinkedCount = 0;
    for (let aIdx = 0; aIdx < annotations.length; aIdx++) {
      const a = annotations[aIdx];
      if (!a.linkedEntityId) unlinkedCount++;
      if (!a.comment) continue;
      const label = a.linkedEntityId
        ? (measurementsStore.byId[a.linkedEntityId]?.annotationName ?? '')
        : `#${unlinkedCount}`;
      comments.push({
        label,
        comment: a.comment,
        measurementId: a.linkedEntityId ?? null,
        imageIndex: imgIdx,
        annotationIndex: aIdx,
      });
    }
  }
  return comments;
}

export type MeasurementChart = Omit<MChartDto, 'id'> & {
  id: number;
  copyOfId?: number;
};

export type MeasurementChartDraft = Omit<MeasurementChart, 'id'> & {
  id: number | 'new' | 'copy';
};

export interface NewMChartProduct {
  genders: Gender[];
  sizeSystemVersionId?: number;
  sizes: { sizeId: number }[];
  hierarchyNodeId: number;
}

export function newMeasurementChart(product: NewMChartProduct): MeasurementChartDraft {
  const initialStatusId = useMChartStatuses().loaded[0].id;
  return {
    id: 'new',
    name: '',
    referenceNumber: undefined,
    genders: [...product.genders],
    sizeSystemVersionId: product.sizeSystemVersionId ?? useHierarchy().getDefaultSizeSystemVersion(product)?.id,
    sizeSystemVersionSizeIds: product.sizes.map((ps) => ps.sizeId),
    modelSizeSystemVersionSizeId: undefined,
    exports: [],
    tagIds: [],
    image: undefined,
    products: [],
    latestVersionStatusId: initialStatusId,
    sections: [{ id: newSectionId([]) }],
    measurements: [],
    versions: [
      {
        versionNumber: 1,
        name: 'Version 1',
        comment: '',
        statusId: initialStatusId,
        createdAt: new Date().toISOString(),
        measurementValues: {},
        files: [],
        images: [],
        measurementComments: {},
        reports: [],
        reportComments: [],
        reportImages: [],
      },
    ],
  };
}

// A section added in the editor has a negative id until it is saved
export function newSectionId(sections: MChartSectionDto[]) {
  return Math.min(0, ...sections.map((s) => s.id)) - 1;
}

// A save gives what it created ids: sections, and the versions' files and
// images. When its reply isn't taken, as there were edits meanwhile, those
// ids are copied over, or the next save would create them again. Sections go
// by position (the reply keeps the order sent), files and images by path.
export function adoptSavedIds(
  chart: Pick<MChartDto, 'sections' | 'measurements' | 'versions'>,
  sentSections: MChartSectionDto[],
  saved: Pick<MChartDto, 'sections' | 'versions'>
) {
  if (sentSections.length === saved.sections.length) {
    const ids = new Map(sentSections.flatMap((s, i) => (s.id < 0 ? [[s.id, saved.sections[i].id] as const] : [])));
    for (const section of chart.sections) section.id = ids.get(section.id) ?? section.id;
    for (const measurement of chart.measurements)
      measurement.sectionId = ids.get(measurement.sectionId) ?? measurement.sectionId;
  }

  for (const version of chart.versions) {
    const savedVersion = saved.versions.find((v) => v.versionNumber === version.versionNumber);
    if (!savedVersion) continue;
    for (const file of version.files) file.id ||= savedVersion.files.find((f) => f.path === file.path)?.id ?? file.id;
    for (const image of version.images)
      image.id ||= savedVersion.images.find((i) => i.path === image.path)?.id ?? image.id;
  }
}

export function sectionsForSave(sections: MChartSectionDto[]): UpdateArgSection[] {
  return sections.map((s) => ({ id: s.id > 0 ? s.id : undefined, name: s.name }));
}

// Each measurement names its section by index into the sections
export function measurementsForSave(chart: Pick<MChartDto, 'sections' | 'measurements'>): UpdateArgMeasurement[] {
  return chart.measurements.map((m) => ({
    measurementId: m.measurementId,
    tolerance: m.tolerance,
    sectionIndex: chart.sections.findIndex((s) => s.id === m.sectionId),
  }));
}

export function cleanMeasurementValuesForSave<T extends MeasurementChartDraft>(
  chart: T
): Omit<T, 'sections' | 'measurements'> & { sections: UpdateArgSection[]; measurements: UpdateArgMeasurement[] } {
  const validSizeIds = new Set(chart.sizeSystemVersionSizeIds);
  const validMeasurementIds = new Set(chart.measurements.map((m) => m.measurementId));

  const cleaned = cloneDeep(chart);

  for (const version of cleaned.versions) {
    for (const measurementId in version.measurementValues) {
      if (!validMeasurementIds.has(Number(measurementId))) {
        delete version.measurementValues[measurementId];
        continue;
      }

      const sizeValues = version.measurementValues[measurementId];
      const filteredSizeValues: { [key: string]: number } = {};
      for (const sizeId in sizeValues) {
        if (validSizeIds.has(Number(sizeId))) {
          filteredSizeValues[sizeId] = sizeValues[sizeId];
        }
      }

      version.measurementValues[measurementId] = filteredSizeValues;
    }

    // Clean measurement comments for deleted measurements
    for (const measurementId in version.measurementComments) {
      if (!validMeasurementIds.has(Number(measurementId))) {
        delete version.measurementComments[measurementId];
      }
    }
  }

  return { ...cleaned, sections: sectionsForSave(cleaned.sections), measurements: measurementsForSave(cleaned) };
}

// files-list / mchart-images-section insert `{ uploading: true, … }` placeholders
// (no path/id yet) directly into the version's files/images while an upload is in
// flight. Those must never be persisted, so callers skip autosave while any exist —
// the upload's completion replaces the placeholder and re-fires the deep watch, so
// the save runs again with valid data.
export function isUploadingMedia(item: unknown): boolean {
  return !!item && typeof item === 'object' && (item as { uploading?: unknown }).uploading === true;
}

export function hasUploadingMedia(versions: { files: unknown[]; images: unknown[] }[]): boolean {
  return versions.some((v) => v.files.some(isUploadingMedia) || v.images.some(isUploadingMedia));
}

export function showMChartSaveError(err: unknown) {
  const message = parseErrorPayload(err)?.message || "Couldn't save the measurement list.";
  showSnackBarNotification(message, { type: 'error' });
}

// The articles of a product that follow a list
export function articlesFollowingList(productId: number, chartId: number) {
  return (useArticles().productArticles[productId] ?? []).filter((a) => a.mChartId === chartId);
}

export function isFollowedByAllArticles(productId: number, chartId: number) {
  const articles = useArticles().productArticles[productId] ?? [];
  return articles.length > 0 && articles.every((a) => a.mChartId === chartId);
}

// The list's sizes as a range, e.g. "XS–XL"
export function listSizeRange(chart: Pick<MChartDto, 'sizeSystemVersionSizeIds' | 'sizeSystemVersionId'>) {
  const sizeSystems = useSizeSystems();
  const sizeValues = chart.sizeSystemVersionSizeIds.map((id) => sizeSystems.getSizeValueById(id));
  return sizeSystems.formatRange(sizeValues, chart.sizeSystemVersionId);
}

// The other products a list is shared with
export function otherProductNames(chart: Pick<MChartDto, 'products'>, productName: string): string[] {
  return (chart.products ?? []).map((p) => p.name).filter((n) => n !== productName);
}

// The stored version number can disagree with the name users gave the version (it keeps
// counting past deleted versions), so lists can be set to show versions by name only.
type VersionNaming = Pick<MChartVersionDto, 'versionNumber' | 'name'>;

// Short form, for headers and badges
export function versionTag(version: VersionNaming, showNumber: boolean): string {
  return showNumber ? `V${version.versionNumber}` : version.name;
}

export function versionLabel(version: VersionNaming, showNumber: boolean): string {
  return showNumber ? `V${version.versionNumber} · ${version.name}` : version.name;
}

// A copy can start over from the source's latest version: a single version, numbered and
// named as the first, carrying the latest version's values, comments, images and files.
export function onlyLatestVersion<T extends VersionNaming>(versions: T[]): T[] {
  const latest = maxBy(versions, (v) => v.versionNumber);
  return latest ? [{ ...latest, versionNumber: 1, name: 'Version 1' }] : [];
}

// A saved list copied as a new one named "… (Copy)", ready for AddMChart
export function copyOfChart(source: MChartDto, latestVersionOnly: boolean) {
  return cleanMeasurementValuesForSave<MeasurementChartDraft>({
    ...source,
    id: 'copy',
    copyOfId: source.id,
    name: `${source.name} (Copy)`,
    versions: latestVersionOnly ? onlyLatestVersion(source.versions) : source.versions,
  });
}
